import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiDollarSign, FiUser, FiTruck, FiFileText, FiCheck, FiPlus, FiPercent, FiCreditCard } from 'react-icons/fi';
import { FaCar, FaCalculator, FaHandshake } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import DealFormsPanel from './DealFormsPanel';
import { modelFromWizard } from '../../config/dealPacketForms';
import { settingsService } from '../../services/settingsService';

const DealBuilder = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [savedDealId, setSavedDealId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // { kind: 'ok'|'err', text }
  const [formsModel, setFormsModel] = useState(null);
  const [dealData, setDealData] = useState({
    customer: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      dateOfBirth: '',
      ssn: '',
      driversLicense: '',
      monthlyIncome: 0,
      creditScore: 0,
      employer: '',
      employmentDuration: ''
    },
    vehicle: {
      id: '',
      year: '',
      make: '',
      model: '',
      vin: '',
      msrp: 0,
      invoicePrice: 0
    },
    pricing: {
      vehiclePrice: 0,
      discount: 0,
      rebates: 0,
      sellingPrice: 0,
      docFee: 225,        // overwritten on mount from Admin > Settings
      registrationFee: 150,
      salesTaxRate: 6.25, // TX motor vehicle sales tax; also from Settings
      salesTaxAmount: 0
    },
    trade: {
      hasTradeIn: false,
      year: '',
      make: '',
      model: '',
      mileage: 0,
      acv: 0,
      payoff: 0,
      netValue: 0
    },
    products: {
      warranty: { selected: false, price: 1995, cost: 1200, name: 'Extended Warranty 5yr/60k' },
      gap: { selected: false, price: 795, cost: 400, name: 'GAP Insurance' },
      maintenance: { selected: false, price: 995, cost: 600, name: 'Maintenance Plan 3yr/36k' }
    },
    financing: {
      method: 'finance',
      downPayment: 0,
      termMonths: 72,
      apr: 5.9,
      monthlyPayment: 0,
      amountFinanced: 0
    },
    totals: {
      totalSalePrice: 0,
      totalOutTheDoor: 0,
      grossProfit: 0,
      financeReserve: 0,
      backendGross: 0,
      totalGross: 0
    }
  });

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [lenders, setLenders] = useState([]);

  useEffect(() => {
    loadVehicles();
    loadCustomers();
    loadLeads();
    loadLenders();
    seedDefaultsFromSettings();
  }, []);

  useEffect(() => {
    calculateDeal();
  }, [dealData.pricing.vehiclePrice, dealData.pricing.discount, dealData.pricing.rebates,
  dealData.pricing.docFee, dealData.pricing.registrationFee, dealData.pricing.salesTaxRate,
  dealData.products,
  dealData.trade.hasTradeIn, dealData.trade.acv, dealData.trade.payoff,
  dealData.financing.downPayment, dealData.financing.apr, dealData.financing.termMonths]);

  // Build (or refresh) the forms model whenever the Review step opens. The
  // wizard owns buyer/vehicle/trade/pricing; edits made inside the forms
  // panel (fees, payment schedule, overrides) survive round-trips.
  useEffect(() => {
    if (activeStep !== 6) return;
    setFormsModel((prev) => {
      const fresh = modelFromWizard(dealData, selectedVehicle);
      if (!prev) return fresh;
      return {
        ...fresh,
        saleDate: prev.saleDate,
        salesperson: prev.salesperson,
        dealerRep: prev.dealerRep,
        money: {
          ...prev.money,
          salePrice: fresh.money.salePrice,
          accessoriesTotal: fresh.money.accessoriesTotal,
          docFee: fresh.money.docFee,
          taxRate: fresh.money.taxRate,
          cashDown: fresh.money.cashDown,
          apr: fresh.money.apr,
          numPayments: prev.money.numPayments || fresh.money.numPayments,
        },
        overrides: prev.overrides,
      };
    });
  }, [activeStep]);

  // Doc fee, tax rate and finance rate come from Admin > Settings so the
  // numbers a new deal starts with match what the dealership configured.
  // Runs once on mount and only fills the untouched initial values, so it can
  // never overwrite something already typed into the wizard.
  const defaultsSeeded = useRef(false);
  const seedDefaultsFromSettings = async () => {
    if (defaultsSeeded.current) return;
    defaultsSeeded.current = true;
    const { data } = await settingsService.getSettings();
    if (!data) return;
    // A setting that is null/blank means "not set" — keep the wizard's own
    // default rather than seeding a zero into a deal.
    const seed = (value, fallback) =>
      value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
        ? fallback
        : Number(value);
    setDealData((prev) => {
      if (prev.pricing.vehiclePrice || prev.customer.firstName) return prev;
      return {
        ...prev,
        pricing: {
          ...prev.pricing,
          docFee: seed(data.docFee, prev.pricing.docFee),
          salesTaxRate: seed(data.salesTaxRate, prev.pricing.salesTaxRate),
        },
        financing: {
          ...prev.financing,
          apr: seed(data.defaultFinanceRate, prev.financing.apr),
        },
      };
    });
  };

  const loadVehicles = async () => {
    const { data } = await supabase
      .from('vehicles_db2025')
      .select('*')
      .neq('status', 'sold')
      .order('year', { ascending: false });
    setVehicles(data || []);
  };

  const loadCustomers = async () => {
    const { data } = await supabase
      .from('customers')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);
    setCustomers(data || []);
  };

  const loadLeads = async () => {
    const { data } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });
    setLeads(data || []);
  };

  const loadLenders = async () => {
    const { data } = await supabase
      .from('lenders')
      .select('*')
      .eq('active', true)
      .order('name');
    setLenders(data || []);
  };

  const handleTradeVinDecode = async () => {
    if (!dealData.trade.vin || dealData.trade.vin.length < 17) {
      alert('Please enter a valid 17-character VIN');
      return;
    }

    try {
      const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${dealData.trade.vin}?format=json`);
      const data = await response.json();

      if (data.Results) {
        const getValue = (variableId) => {
          const result = data.Results.find(r => r.VariableId === variableId);
          return result?.Value || '';
        };

        setDealData({
          ...dealData,
          trade: {
            ...dealData.trade,
            year: getValue(29) || dealData.trade.year,
            make: getValue(26) || dealData.trade.make,
            model: getValue(28) || dealData.trade.model
          }
        });

        alert('VIN decoded successfully!');
      }
    } catch (error) {
      console.error('Error decoding VIN:', error);
      alert('Failed to decode VIN. Please enter vehicle details manually.');
    }
  };

  const saveDeal = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      // Calculate monthly payment before saving
      const { pricing, trade, products, financing } = dealData;
      const sellingPrice = pricing.vehiclePrice - pricing.discount - pricing.rebates;
      const tradeNetValue = trade.hasTradeIn ? trade.acv - trade.payoff : 0;
      const productTotal = Object.values(products).reduce((sum, p) => p.selected ? sum + p.price : sum, 0);
      const taxableAmount = sellingPrice - (tradeNetValue > 0 ? tradeNetValue : 0);
      const salesTaxAmount = (taxableAmount * pricing.salesTaxRate) / 100;
      const totalSalePrice = sellingPrice + productTotal + pricing.docFee + pricing.registrationFee + salesTaxAmount;
      const amountFinanced = totalSalePrice - financing.downPayment - (tradeNetValue > 0 ? tradeNetValue : 0);

      const monthlyRate = financing.apr / 100 / 12;
      const monthlyPayment = monthlyRate > 0
        ? (amountFinanced * monthlyRate * Math.pow(1 + monthlyRate, financing.termMonths)) /
        (Math.pow(1 + monthlyRate, financing.termMonths) - 1)
        : amountFinanced / financing.termMonths;

      const payload = {
        customer_name: `${dealData.customer.firstName} ${dealData.customer.lastName}`,
        customer_email: dealData.customer.email,
        customer_phone: dealData.customer.phone,
        vehicle_id: dealData.vehicle.id || null,
        vehicle_price: dealData.pricing.vehiclePrice || 0,
        trade_in_value: dealData.trade.acv || 0,
        trade_in_payoff: dealData.trade.payoff || 0,
        down_payment: dealData.financing.downPayment || 0,
        monthly_payment: monthlyPayment || 0,
        total_price: totalSalePrice || 0,
        gross_profit: dealData.totals?.totalGross || 0,
        status: 'pending'
      };
      if (!savedDealId) payload.created_at = new Date().toISOString();
      if (formsModel) payload.forms_data = formsModel;

      // First save inserts; later saves update the same deal instead of
      // creating a duplicate row.
      const write = (p) =>
        savedDealId
          ? supabase.from('deals').update(p).eq('id', savedDealId).select().single()
          : supabase.from('deals').insert([p]).select().single();

      let { data, error } = await write(payload);

      if (error && payload.forms_data &&
        (error.code === 'PGRST204' || (error.message || '').includes('forms_data'))) {
        // deals.forms_data column not migrated yet — save the deal without it
        delete payload.forms_data;
        ({ data, error } = await write(payload));
      }

      if (error) throw error;

      if (data && data.id) {
        const wasUpdate = !!savedDealId;
        setSavedDealId(data.id);
        setSaveStatus({
          kind: 'ok',
          text: wasUpdate
            ? 'Deal updated.'
            : 'Deal saved — it now shows under Recent Deals.'
        });
      } else {
        setSaveStatus({ kind: 'ok', text: 'Deal saved, but no ID came back. Check Recent Deals before saving again.' });
      }

    } catch (error) {
      console.error('Error saving deal:', error);
      setSaveStatus({ kind: 'err', text: 'Failed to save deal: ' + (error.message || 'Please check all required fields.') });
    } finally {
      setSaving(false);
    }
  };

  const calculateDeal = () => {
    const { pricing, trade, products, financing } = dealData;

    const sellingPrice = pricing.vehiclePrice - pricing.discount - pricing.rebates;
    const tradeNetValue = trade.hasTradeIn ? trade.acv - trade.payoff : 0;
    const productTotal = Object.values(products).reduce((sum, p) => p.selected ? sum + p.price : sum, 0);
    const taxableAmount = sellingPrice - (tradeNetValue > 0 ? tradeNetValue : 0);
    const salesTaxAmount = (taxableAmount * pricing.salesTaxRate) / 100;
    const totalSalePrice = sellingPrice + productTotal + pricing.docFee + pricing.registrationFee + salesTaxAmount;
    const amountFinanced = totalSalePrice - financing.downPayment - (tradeNetValue > 0 ? tradeNetValue : 0);

    const monthlyRate = financing.apr / 100 / 12;
    const monthlyPayment = monthlyRate > 0
      ? (amountFinanced * monthlyRate * Math.pow(1 + monthlyRate, financing.termMonths)) /
      (Math.pow(1 + monthlyRate, financing.termMonths) - 1)
      : amountFinanced / financing.termMonths;

    const vehicleGross = sellingPrice - (dealData.vehicle.invoicePrice || pricing.vehiclePrice * 0.92);
    const productGross = Object.values(products).reduce((sum, p) => p.selected ? sum + (p.price - p.cost) : sum, 0);
    const financeReserve = financing.method === 'finance' ? amountFinanced * 0.02 : 0;

    setDealData(prev => ({
      ...prev,
      pricing: { ...prev.pricing, sellingPrice, salesTaxAmount },
      totals: {
        totalSalePrice,
        totalOutTheDoor: totalSalePrice,
        productTotal,
        amountFinanced,
        monthlyPayment: monthlyPayment || 0,
        grossProfit: vehicleGross,
        financeReserve,
        backendGross: productGross,
        totalGross: vehicleGross + productGross + financeReserve
      }
    }));
  };

  const steps = [
    { number: 1, title: 'Customer', icon: FiUser },
    { number: 2, title: 'Vehicle', icon: FaCar },
    { number: 3, title: 'Trade-In', icon: FiTruck },
    { number: 4, title: 'F&I', icon: FiFileText },
    { number: 5, title: 'Finance', icon: FiCreditCard },
    { number: 6, title: 'Review', icon: FaHandshake }
  ];

  return (
    <div className="max-w-[1400px]">
      <div className="mb-5">
        <h2 className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">Deal Builder</h2>
        <p className="mt-1 text-[13px] text-adm-text-mid">Structure complete vehicle deals</p>
      </div>

      {/* Progress Steps — presentation only; step order and state are unchanged */}
      <nav aria-label="Deal progress" className="mb-5">
        <ol className="flex items-center gap-1 overflow-x-auto pb-1">
          {steps.map((step, i) => {
            const done = activeStep > step.number;
            const current = activeStep === step.number;
            return (
              <li key={step.number} className="flex items-center shrink-0">
                <button
                  type="button"
                  onClick={() => setActiveStep(step.number)}
                  aria-current={current ? 'step' : undefined}
                  className="flex items-center gap-2 rounded-adm-ctl px-2 py-1 transition-colors duration-150 hover:bg-adm-bg-2"
                >
                  <span
                    className={`flex items-center justify-center w-7 h-7 rounded-full shrink-0 transition-colors duration-150 ${
                      done
                        ? 'bg-adm-blue text-white'
                        : current
                        ? 'bg-adm-blue-tint text-adm-text-hi ring-2 ring-adm-blue'
                        : 'bg-adm-bg-2 text-adm-text-lo'
                    }`}
                  >
                    <SafeIcon icon={step.icon} className="w-3.5 h-3.5" />
                  </span>
                  <span
                    className={`text-[13px] whitespace-nowrap ${
                      current
                        ? 'text-adm-text-hi font-medium'
                        : done
                        ? 'text-adm-text-mid'
                        : 'text-adm-text-lo'
                    }`}
                  >
                    {step.title}
                  </span>
                </button>
                {i < steps.length - 1 && (
                  <span
                    className={`h-px w-6 mx-1 shrink-0 ${done ? 'bg-adm-blue' : 'bg-adm-border'}`}
                    aria-hidden="true"
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Form */}
        <div className="lg:col-span-2">
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
            className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-5"
          >
            {activeStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-[15px] font-semibold text-adm-text-hi mb-4">Customer Information</h3>

                {/* Customer Search */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-adm-text-mid mb-1">Search Existing Customer or Lead</label>
                  <input
                    type="text"
                    placeholder="Search by name, phone, or email..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="w-full mb-2"
                  />

                  {customerSearch && (
                    <div className="rounded-adm-ctl max-h-60 overflow-y-auto shadow-[inset_0_0_0_1px_oklch(var(--adm-border))]">
                      {/* Search in Leads */}
                      {leads
                        .filter(lead => {
                          const search = customerSearch.toLowerCase();
                          return (
                            (lead.first_name?.toLowerCase().includes(search)) ||
                            (lead.last_name?.toLowerCase().includes(search)) ||
                            (lead.email?.toLowerCase().includes(search)) ||
                            (lead.phone?.includes(search))
                          );
                        })
                        .slice(0, 5)
                        .map(lead => (
                          <div
                            key={`lead-${lead.id}`}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b"
                            onClick={() => {
                              setSelectedCustomer(lead);
                              setDealData({
                                ...dealData,
                                customer: {
                                  firstName: lead.first_name || '',
                                  lastName: lead.last_name || '',
                                  email: lead.email || '',
                                  phone: lead.phone || '',
                                  address: lead.address || '',
                                  city: lead.city || '',
                                  state: lead.state || '',
                                  zipCode: lead.zip_code || '',
                                  monthlyIncome: lead.monthly_income || 0,
                                  creditScore: lead.credit_score_range ? parseInt(lead.credit_score_range.split('-')[0]) : 0
                                }
                              });
                              setCustomerSearch('');
                            }}
                          >
                            <div className="font-medium">
                              {lead.first_name} {lead.last_name}
                              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Lead</span>
                            </div>
                            <div className="text-sm text-gray-600">
                              {lead.phone} • {lead.email}
                            </div>
                          </div>
                        ))}

                      {/* Search in existing Customers */}
                      {customers
                        .filter(customer => {
                          const search = customerSearch.toLowerCase();
                          return (
                            (customer.first_name?.toLowerCase().includes(search)) ||
                            (customer.last_name?.toLowerCase().includes(search)) ||
                            (customer.email?.toLowerCase().includes(search)) ||
                            (customer.phone?.includes(search))
                          );
                        })
                        .slice(0, 5)
                        .map(customer => (
                          <div
                            key={`customer-${customer.id}`}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b"
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setDealData({
                                ...dealData,
                                customer: {
                                  firstName: customer.first_name || '',
                                  lastName: customer.last_name || '',
                                  email: customer.email || '',
                                  phone: customer.phone || '',
                                  address: customer.address || '',
                                  city: customer.city || '',
                                  state: customer.state || '',
                                  zipCode: customer.zip_code || '',
                                  dateOfBirth: customer.date_of_birth || '',
                                  ssn: customer.ssn || '',
                                  driversLicense: customer.drivers_license || '',
                                  monthlyIncome: customer.monthly_income || 0,
                                  creditScore: customer.credit_score || 0,
                                  employer: customer.employer || '',
                                  employmentDuration: customer.employment_duration || ''
                                }
                              });
                              setCustomerSearch('');
                            }}
                          >
                            <div className="font-medium">
                              {customer.first_name} {customer.last_name}
                              <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Customer</span>
                            </div>
                            <div className="text-sm text-gray-600">
                              {customer.phone} • {customer.email}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {selectedCustomer && (
                    <div className="bg-blue-50 p-3 rounded-lg mb-2">
                      <div className="text-sm text-blue-800">
                        Selected: <strong>{selectedCustomer.first_name} {selectedCustomer.last_name}</strong>
                        <button
                          onClick={() => {
                            setSelectedCustomer(null);
                            setDealData({
                              ...dealData, customer: {
                                firstName: '',
                                lastName: '',
                                email: '',
                                phone: '',
                                monthlyIncome: 0,
                                creditScore: 0
                              }
                            });
                          }}
                          className="ml-2 text-blue-600 hover:text-blue-700"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={dealData.customer.firstName}
                    onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, firstName: e.target.value } })}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={dealData.customer.lastName}
                    onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, lastName: e.target.value } })}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={dealData.customer.email}
                    onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, email: e.target.value } })}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={dealData.customer.phone}
                    onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, phone: e.target.value } })}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Street Address"
                    value={dealData.customer.address || ''}
                    onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, address: e.target.value } })}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="City"
                    value={dealData.customer.city || ''}
                    onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, city: e.target.value } })}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="State"
                    value={dealData.customer.state || ''}
                    onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, state: e.target.value } })}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="ZIP Code"
                    value={dealData.customer.zipCode || ''}
                    onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, zipCode: e.target.value } })}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Driver's License #"
                    value={dealData.customer.driversLicense || ''}
                    onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, driversLicense: e.target.value } })}
                    className="px-4 py-2 border rounded-lg"
                  />
                  <div>
                    <label className="block text-xs font-medium text-adm-text-mid mb-1">Monthly Income</label>
                    <input
                      type="number"
                      placeholder="Monthly Income"
                      value={dealData.customer.monthlyIncome || ''}
                      onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, monthlyIncome: parseFloat(e.target.value) || 0 } })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-adm-text-mid mb-1">Approx Credit Score</label>
                    <input
                      type="number"
                      placeholder="Approx Credit Score"
                      value={dealData.customer.creditScore || ''}
                      onChange={(e) => setDealData({ ...dealData, customer: { ...dealData.customer, creditScore: parseInt(e.target.value) || 0 } })}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-[15px] font-semibold text-adm-text-hi mb-4">Vehicle Selection</h3>
                <select
                  className="w-full"
                  value={dealData.vehicle.id || ''}
                  onChange={(e) => {
                    const v = vehicles.find(v => v.id.toString() === e.target.value);
                    if (v) {
                      setSelectedVehicle(v);
                      setDealData({
                        ...dealData,
                        vehicle: {
                          id: v.id,
                          year: v.year,
                          make: v.make,
                          model: v.model,
                          vin: v.vin,
                          msrp: v.price,
                          invoicePrice: v.price * 0.92
                        },
                        pricing: { ...dealData.pricing, vehiclePrice: v.price }
                      });
                    }
                  }}
                >
                  <option value="">Select Vehicle from Inventory</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>
                      Stock #{v.stock_number} - {v.year} {v.make} {v.model} - ${v.price?.toLocaleString()} - {v.mileage?.toLocaleString()} miles
                    </option>
                  ))}
                </select>

                {selectedVehicle && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><strong>VIN:</strong> {selectedVehicle.vin}</div>
                      <div><strong>Color:</strong> {selectedVehicle.exterior_color}</div>
                      <div><strong>Engine:</strong> {selectedVehicle.engine}</div>
                      <div><strong>Transmission:</strong> {selectedVehicle.transmission}</div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-adm-text-mid mb-1">Discount</label>
                    <input
                      type="number"
                      value={dealData.pricing.discount}
                      onChange={(e) => setDealData({ ...dealData, pricing: { ...dealData.pricing, discount: parseFloat(e.target.value) || 0 } })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-adm-text-mid mb-1">Rebates</label>
                    <input
                      type="number"
                      value={dealData.pricing.rebates}
                      onChange={(e) => setDealData({ ...dealData, pricing: { ...dealData.pricing, rebates: parseFloat(e.target.value) || 0 } })}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeStep === 3 && (
              <div className="space-y-4">
                <h3 className="text-[15px] font-semibold text-adm-text-hi mb-4">Trade-In</h3>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={dealData.trade.hasTradeIn}
                    onChange={(e) => setDealData({ ...dealData, trade: { ...dealData.trade, hasTradeIn: e.target.checked } })}
                  />
                  <span>Customer has trade-in</span>
                </label>

                {dealData.trade.hasTradeIn && (
                  <div className="space-y-4 mt-4">
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h4 className="font-medium text-gray-900 mb-3">Trade-In Vehicle Details</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-adm-text-mid mb-1">Year</label>
                          <input
                            type="number"
                            placeholder="2020"
                            value={dealData.trade.year || ''}
                            onChange={(e) => setDealData({ ...dealData, trade: { ...dealData.trade, year: e.target.value } })}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-adm-text-mid mb-1">Make</label>
                          <input
                            type="text"
                            placeholder="Toyota"
                            value={dealData.trade.make || ''}
                            onChange={(e) => setDealData({ ...dealData, trade: { ...dealData.trade, make: e.target.value } })}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-adm-text-mid mb-1">Model</label>
                          <input
                            type="text"
                            placeholder="Camry"
                            value={dealData.trade.model || ''}
                            onChange={(e) => setDealData({ ...dealData, trade: { ...dealData.trade, model: e.target.value } })}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-adm-text-mid mb-1">Mileage</label>
                          <input
                            type="number"
                            placeholder="50000"
                            value={dealData.trade.mileage || ''}
                            onChange={(e) => setDealData({ ...dealData, trade: { ...dealData.trade, mileage: parseInt(e.target.value) || 0 } })}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-adm-text-mid mb-1">VIN</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="1HGCV1F3XJA123456"
                              value={dealData.trade.vin || ''}
                              onChange={(e) => setDealData({ ...dealData, trade: { ...dealData.trade, vin: e.target.value } })}
                              onBlur={() => handleTradeVinDecode()}
                              className="flex-1 px-3 py-2 border rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={handleTradeVinDecode}
                              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                              Decode
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-adm-text-mid mb-1">Condition</label>
                          <select
                            value={dealData.trade.condition || 'good'}
                            onChange={(e) => setDealData({ ...dealData, trade: { ...dealData.trade, condition: e.target.value } })}
                            className="w-full"
                          >
                            <option value="excellent">Excellent</option>
                            <option value="good">Good</option>
                            <option value="fair">Fair</option>
                            <option value="poor">Poor</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-adm-text-mid mb-1">Trade ACV (Actual Cash Value)</label>
                        <input
                          type="number"
                          placeholder="15000"
                          value={dealData.trade.acv}
                          onChange={(e) => setDealData({ ...dealData, trade: { ...dealData.trade, acv: parseFloat(e.target.value) || 0 } })}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-adm-text-mid mb-1">Payoff Amount</label>
                        <input
                          type="number"
                          placeholder="8000"
                          value={dealData.trade.payoff}
                          onChange={(e) => setDealData({ ...dealData, trade: { ...dealData.trade, payoff: parseFloat(e.target.value) || 0 } })}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeStep === 4 && (
              <div className="space-y-4">
                <h3 className="text-[15px] font-semibold text-adm-text-hi mb-4">F&I Products</h3>
                {Object.entries(dealData.products).map(([key, product]) => (
                  <label key={key} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={product.selected}
                        onChange={(e) => setDealData({
                          ...dealData,
                          products: { ...dealData.products, [key]: { ...product, selected: e.target.checked } }
                        })}
                      />
                      <span>{product.name}</span>
                    </div>
                    <span className="font-semibold">${product.price}</span>
                  </label>
                ))}
              </div>
            )}

            {activeStep === 5 && (
              <div className="space-y-4">
                <h3 className="text-[15px] font-semibold text-adm-text-hi mb-4">Financing</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-adm-text-mid mb-1">Deal Type</label>
                    <select
                      value={dealData.financing.method}
                      onChange={(e) => setDealData({ ...dealData, financing: { ...dealData.financing, method: e.target.value } })}
                      className="w-full"
                    >
                      <option value="cash">Cash</option>
                      <option value="finance">Financed (lender)</option>
                      <option value="bhph">Buy Here Pay Here</option>
                      <option value="wholesale">Wholesale</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-adm-text-mid mb-1">Term (months)</label>
                    <input
                      type="number"
                      value={dealData.financing.termMonths}
                      onChange={(e) => setDealData({ ...dealData, financing: { ...dealData.financing, termMonths: parseInt(e.target.value) || 0 } })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-adm-text-mid mb-1">Down Payment</label>
                    <input
                      type="number"
                      value={dealData.financing.downPayment}
                      onChange={(e) => setDealData({ ...dealData, financing: { ...dealData.financing, downPayment: parseFloat(e.target.value) || 0 } })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-adm-text-mid mb-1">APR %</label>
                    <input
                      type="number"
                      step="0.1"
                      value={dealData.financing.apr}
                      onChange={(e) => setDealData({ ...dealData, financing: { ...dealData.financing, apr: parseFloat(e.target.value) || 0 } })}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeStep === 6 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold">Review & Finish</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Check the numbers, print the paperwork, then save the deal.
                  </p>
                </div>

                <div className="grid grid-cols-3 divide-x rounded-lg border bg-gray-50">
                  <div className="px-4 py-3">
                    <div className="text-xs text-gray-500">Total out the door</div>
                    <div className="text-lg font-semibold">${(dealData.totals?.totalOutTheDoor || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  </div>
                  <div className="px-4 py-3">
                    <div className="text-xs text-gray-500">Monthly payment</div>
                    <div className="text-lg font-semibold">${(dealData.totals?.monthlyPayment || 0).toFixed(2)}</div>
                  </div>
                  <div className="px-4 py-3">
                    <div className="text-xs text-gray-500">Gross profit</div>
                    <div className="text-lg font-semibold text-green-700">${(dealData.totals?.totalGross || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  </div>
                </div>

                {/* Auto-filled dealer paperwork for this deal */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Paperwork</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Packets fill themselves from this deal — review, adjust, print.
                  </p>
                  {formsModel && (
                    <DealFormsPanel
                      model={formsModel}
                      onModelChange={(next) => {
                        setFormsModel(next);
                        // Keep the wizard's deal type (step 5) in sync with the
                        // panel's chips so there is one source of truth.
                        const method = next.dealType === 'financed' ? 'finance' : next.dealType;
                        if (method && method !== dealData.financing.method) {
                          setDealData((prev) => ({
                            ...prev,
                            financing: { ...prev.financing, method }
                          }));
                        }
                      }}
                    />
                  )}
                </div>

                {saveStatus && (
                  <div className={`text-sm rounded-lg px-4 py-3 ${saveStatus.kind === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                    }`}>
                    {saveStatus.text}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between mt-6">
              <button
                onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                disabled={activeStep === 1}
                className="px-6 py-2 border rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              {activeStep < 6 ? (
                <button
                  onClick={() => setActiveStep(activeStep + 1)}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={saveDeal}
                  disabled={saving}
                  className="px-8 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold disabled:opacity-50"
                >
                  {saving ? 'Saving…' : savedDealId ? 'Update Deal' : 'Save Deal'}
                </button>
              )}
            </div>
          </motion.div>
        </div>

        {/* Summary Panel */}
        <div className="lg:col-span-1">
          <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-6 sticky top-20">
            <h3 className="text-[15px] font-semibold text-adm-text-hi mb-4">Deal Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Vehicle</span>
                <span>${dealData.pricing.vehiclePrice.toLocaleString()}</span>
              </div>
              {dealData.pricing.discount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Discount</span>
                  <span>-${dealData.pricing.discount.toLocaleString()}</span>
                </div>
              )}
              {dealData.pricing.rebates > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Rebates</span>
                  <span>-${dealData.pricing.rebates.toLocaleString()}</span>
                </div>
              )}
              {Object.entries(dealData.products)
                .filter(([, p]) => p.selected)
                .map(([key, p]) => (
                  <div key={key} className="flex justify-between">
                    <span>{p.name}</span>
                    <span>${p.price.toLocaleString()}</span>
                  </div>
                ))}
              <div className="flex justify-between">
                <span>Doc fee</span>
                <span>${dealData.pricing.docFee.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Registration</span>
                <span>${dealData.pricing.registrationFee.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Sales tax ({dealData.pricing.salesTaxRate}%)</span>
                <span>${(dealData.pricing.salesTaxAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-2 text-base">
                <span>Total</span>
                <span>${dealData.totals.totalOutTheDoor.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              </div>
              {dealData.trade.hasTradeIn && (
                <div className="flex justify-between text-red-600">
                  <span>Trade equity</span>
                  <span>-${(dealData.trade.acv - dealData.trade.payoff).toLocaleString()}</span>
                </div>
              )}
              {dealData.financing.downPayment > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Down payment</span>
                  <span>-${dealData.financing.downPayment.toLocaleString()}</span>
                </div>
              )}
              {(dealData.trade.hasTradeIn || dealData.financing.downPayment > 0) && (
                <div className="flex justify-between">
                  <span>Amount financed</span>
                  <span>${(dealData.totals?.amountFinanced || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between text-green-600 font-semibold text-base">
                <span>Monthly</span>
                <span>${(dealData.totals?.monthlyPayment || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealBuilder;
